import { avecLibelleTraduit } from '../i18n/libelles';

/* LA SITUATION D'UNE PARTIE ADVERSE, EN UN SEUL CHOIX.

   L'ecran posait DEUX questions pour dire une seule chose. « Type de dommage »
   - corporel, materiel, mixte - puis, si le dommage touchait une personne,
   « Situation » - blesse, decede, l'Etat, morale privee. Il fallait comprendre
   l'articulation des deux pour saisir qu'un conducteur blesse devient une victime du
   dossier, et le second champ n'apparaissait qu'apres le premier.

   Un seul choix a quatre valeurs les remplace : Blesse, Decede, Materiel, Adversaire.
   Il se lit comme une phrase, et il determine les deux champs du serveur.

   « L'ETAT » ET « MORALE PRIVEE » NE SONT PLUS PROPOSES. Ils designaient la nature
   juridique d'un engin adverse, non une situation, et ne faisaient pas une victime. Les
   fiches qui les portent restent lisibles - `libelleTypeVictime` les traduit encore -
   mais on ne les saisit plus.

   POURQUOI UN CHAMP D'ECRAN, ET NON UN CHAMP DU SERVEUR. Le serveur garde ses deux
   colonnes : `typeVictime`, non nulle, et `typeDommage`, nullable. Les fusionner
   demanderait une migration et reecrirait l'historique. Ce module traduit, dans un sens
   et dans l'autre, et le contrat ne bouge pas. */

/** Les quatre situations, pour un engin adverse. Libelles lus a l'affichage. */
export const SITUATIONS_ADVERSE = avecLibelleTraduit([
  { value: 'BLESSE' },
  { value: 'DECEDE' },
  { value: 'MATERIEL' },
  { value: 'ADVERSAIRE' },
], 'commun:adversaireModal.situations');

/**
 * Les situations proposees selon la nature de la partie adverse.
 *
 * <p>UN PIETON N'A PAS DE SITUATION « MATERIEL ». Il est une personne : son dommage est
 * corporel par definition, et lui offrir « Materiel » ou « Adversaire » serait proposer
 * un non-sens. On ne lui montre que les deux situations qui le concernent.
 *
 * <p>Un bien - batiment, objet - n'a aucune situation : il n'y a personne.
 */
export function situationsPour({ estPieton, estEngin }) {
  if (estPieton) return SITUATIONS_ADVERSE.filter((s) => s.value === 'BLESSE' || s.value === 'DECEDE');
  if (estEngin) return SITUATIONS_ADVERSE;
  return [];
}

/** La situation designe-t-elle une personne atteinte, donc une victime du dossier ? */
export function estUneVictime(situation) {
  return situation === 'BLESSE' || situation === 'DECEDE';
}

/**
 * Les deux champs du serveur, deduits de la situation choisie.
 *
 * <p>`typeDommage` est NUL pour « Adversaire » : la colonne l'accepte, et c'est la
 * representation juste d'une partie identifiee dont aucun prejudice n'est retenu.
 * Inventer « materiel » lui preterait des degats qu'on n'a pas constates.
 *
 * <p>MIXTE n'est plus produit par cet ecran. Un blesse dont le vehicule est aussi abime
 * decrit ses degats dans le champ de description ; le distinguer par une quatrieme
 * valeur de dommage n'apportait rien au traitement du dossier.
 */
export function champsServeur(situation, { estPieton, estEngin } = {}) {
  if (estUneVictime(situation)) {
    return { typeVictime: situation, typeDommage: 'CORPOREL' };
  }
  if (situation === 'MATERIEL') {
    return { typeVictime: 'NEUTRE', typeDommage: 'MATERIEL' };
  }
  if (situation === 'ADVERSAIRE') {
    return { typeVictime: 'NEUTRE', typeDommage: null };
  }
  /* Aucune situation choisie. Un pieton est corporel par nature, un bien est materiel
     par nature : on ne laisse pas le dossier sans type pour autant. */
  if (estPieton) return { typeVictime: 'NEUTRE', typeDommage: 'CORPOREL' };
  if (estEngin) return { typeVictime: 'NEUTRE', typeDommage: null };
  return { typeVictime: 'NEUTRE', typeDommage: 'MATERIEL' };
}

/**
 * La situation a afficher pour une fiche deja enregistree.
 *
 * <p>C'est la traduction inverse, et elle doit tenir les fiches anciennes : celles qui
 * portent ETAT ou MORALE_PRIVEE, et celles dont le dommage est MIXTE. Une fiche qu'on
 * ouvre doit montrer un choix, sinon l'enregistrer la viderait.
 */
export function situationDepuisFiche(fiche) {
  if (!fiche) return undefined;
  if (estUneVictime(fiche.typeVictime)) return fiche.typeVictime;
  // ETAT et MORALE_PRIVEE ne sont plus des situations : la fiche se relit comme une
  // partie adverse sans prejudice retenu.
  if (fiche.typeDommage === 'MATERIEL' || fiche.typeDommage === 'MIXTE') return 'MATERIEL';
  return 'ADVERSAIRE';
}
